import { jwtDecode } from 'jwt-decode';
import { DecodedAuthToken } from '../../components/authentication/types/AuthTypes';
import { atlasAuthApi } from '../api/atlasAuthApi';

/** Resolved feature flags with optional server config per flag name. */
export type ResolvedFlagMap = Record<string, { enabled: boolean; config: Record<string, unknown> }>;
export interface FeatureFlagGroupConfig {
  members: string[];
  label?: string;
  description?: string;
  priority?: number;
}

export interface ResolvedFeatureFlagGroup {
  name: string;
  enabled: boolean;
  config: FeatureFlagGroupConfig;
}

export interface FeatureFlagGroupResolution {
  groupsByName: Record<string, ResolvedFeatureFlagGroup>;
  memberToGroups: Record<string, string[]>;
  unknownMembersByGroup: Record<string, string[]>;
}

function normalizeConfig(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function asStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const normalized = item.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
}

function asOptionalString(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed || undefined;
}

function asOptionalNumber(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return undefined;
  return raw;
}

function isGroupFlagName(flagName: string): boolean {
  return flagName.startsWith('group_');
}

function parseGroupConfig(config: Record<string, unknown>): FeatureFlagGroupConfig {
  const members = asStringArray(config.members);
  return {
    members,
    label: asOptionalString(config.label),
    description: asOptionalString(config.description),
    priority: asOptionalNumber(config.priority),
  };
}

/**
 * Normalizes JWT or API `flags` payloads to a consistent map.
 */
export function normalizeFlagsClaim(
  raw: DecodedAuthToken['flags'] | Record<string, { enabled?: boolean; config?: unknown }> | undefined
): ResolvedFlagMap {
  if (!raw || typeof raw !== 'object') return {};
  const out: ResolvedFlagMap = {};
  for (const [name, entry] of Object.entries(raw)) {
    if (!entry || typeof entry !== 'object') continue;
    const rec = entry as { enabled?: unknown; config?: unknown };
    out[name] = {
      enabled: rec.enabled === true,
      config: normalizeConfig(rec.config),
    };
  }
  return out;
}

export function resolveFeatureFlagGroups(flags: ResolvedFlagMap): FeatureFlagGroupResolution {
  const groupsByName: Record<string, ResolvedFeatureFlagGroup> = {};
  const memberToGroups: Record<string, string[]> = {};
  const unknownMembersByGroup: Record<string, string[]> = {};
  const knownFlags = new Set(Object.keys(flags));

  for (const [name, entry] of Object.entries(flags)) {
    if (!isGroupFlagName(name)) continue;
    const config = parseGroupConfig(entry.config);
    groupsByName[name] = {
      name,
      enabled: entry.enabled === true,
      config,
    };

    for (const member of config.members) {
      if (!memberToGroups[member]) {
        memberToGroups[member] = [];
      }
      memberToGroups[member].push(name);
      if (!knownFlags.has(member)) {
        if (!unknownMembersByGroup[name]) {
          unknownMembersByGroup[name] = [];
        }
        unknownMembersByGroup[name].push(member);
      }
    }
  }

  return {
    groupsByName,
    memberToGroups,
    unknownMembersByGroup,
  };
}

export function getBlockingGroups(flags: ResolvedFlagMap, flagName: string): string[] {
  const resolution = resolveFeatureFlagGroups(flags);
  return (resolution.memberToGroups[flagName] ?? []).filter(
    (groupName) => resolution.groupsByName[groupName]?.enabled === false
  );
}

export function isFeatureRuntimeEnabled(flags: ResolvedFlagMap, flagName: string): boolean {
  // The individual flag is the single source of truth: a feature is available iff
  // its own entry is `enabled: true`. Synthetic `group_*` flags no longer override
  // member features at runtime — the admin Feature Flags tab fully controls access.
  //
  // Group definitions are still resolved (see resolveFeatureFlagGroups /
  // getBlockingGroups) for admin impact previews and diagnostics, but a disabled
  // group can no longer silently hide a feature that an admin has turned on.
  return flags[flagName]?.enabled === true;
}

export function parseFlagsFromAccessToken(accessToken: string): ResolvedFlagMap {
  try {
    const decoded = jwtDecode<DecodedAuthToken>(accessToken);
    return normalizeFlagsClaim(decoded.flags);
  } catch {
    return {};
  }
}

/** Refresh flags from the backend (same resolution rules as JWT). */
export async function fetchResolvedFeatureFlags(): Promise<ResolvedFlagMap> {
  const res = await atlasAuthApi.get<{
    flags: Record<string, { enabled?: boolean; config?: unknown }>;
  }>('/feature-flags/me');
  return normalizeFlagsClaim(res.data.flags);
}
